import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Modal as AntModal } from 'antd';
import useThemeStore from '@stores/theme.store';

/** sm: compact alerts / confirm — 400px */
const WIDTH_SM = 400;
/** md: short forms (create sprint, create task, add link content) — 640px */
const WIDTH_MD = 640;
/** lg: detail — wide, constrained by equal viewport inset on left/right */
const VIEWPORT_INSET = 24;
const MODAL_MAX_HEIGHT = `calc(100vh - ${VIEWPORT_INSET * 2}px)`;
const MODAL_MAX_WIDTH = `calc(100vw - ${VIEWPORT_INSET * 2}px)`;
const WIDTH_LG = `min(960px, ${MODAL_MAX_WIDTH})`;

/** Above side panel (1050) and other overlays */
const MODAL_Z_INDEX = 1100;

/**
 * Shared shell: rounded card, header/body rhythm, no animation clash.
 * Applied via .app-modal-shell on the modal wrap (sm | md | lg).
 */
const AppModalShellGlobalStyle = createGlobalStyle`
  /*
   * No enter/leave motion — AppModal passes transitionName="" / maskTransitionName="".
   * Belt-and-suspenders: zero duration if rc-motion still attaches classes.
   */
  .ant-modal-wrap.app-modal-shell .ant-modal,
  .ant-modal-wrap.app-modal-shell .ant-modal-mask,
  .ant-modal-wrap.app-modal-shell .ant-modal-content {
    animation: none !important;
  }
  /*
   * Grid row 1: title (minmax) + close (auto) — same baseline row, close flush right.
   * rc-dialog order: close, header, body, footer — explicit grid placement fixes column order.
   */
  .ant-modal-wrap.app-modal-shell .ant-modal-content {
    border-radius: 14px !important;
    overflow: hidden !important;
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) auto !important;
    grid-template-rows: auto minmax(0, 1fr) auto !important;
    align-items: stretch !important;
    max-height: inherit !important;
    padding: 0 !important;
    background: #ffffff !important;
    box-shadow:
      0 25px 50px -12px rgba(0, 0, 0, 0.12),
      0 0 0 1px rgba(0, 0, 0, 0.04) !important;
  }
  .ant-modal-wrap.app-modal-shell .ant-modal-header {
    grid-column: 1 !important;
    grid-row: 1 !important;
    margin: 0 !important;
    min-width: 0 !important;
    display: flex !important;
    align-items: center !important;
    align-self: center !important;
    padding: 14px 16px 14px 20px !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
    background: transparent !important;
  }
  .ant-modal-wrap.app-modal-shell .ant-modal-title {
    font-family: var(--font-family-geist), system-ui, sans-serif;
    font-size: clamp(15px, 2.2vw + 0.35rem, 18px) !important;
    font-weight: 600 !important;
    line-height: 1.25 !important;
    color: rgba(0, 0, 0, 0.88) !important;
    padding-right: 0 !important;
    box-sizing: border-box !important;
    flex: 1 !important;
    min-width: 0 !important;
  }
  .ant-modal-wrap.app-modal-shell .ant-modal-body {
    grid-column: 1 / -1 !important;
    grid-row: 2 !important;
    min-height: 0 !important;
    width: 100% !important;
    box-sizing: border-box !important;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    overscroll-behavior: contain !important;
    -webkit-overflow-scrolling: touch !important;
    padding: 16px 20px !important;
    font-family: var(--font-family-geist), system-ui, sans-serif;
    font-size: clamp(12px, 0.35vw + 11px, 14px) !important;
    line-height: 1.45 !important;
    background: transparent !important;
    color: rgba(0, 0, 0, 0.88) !important;
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.22) transparent;
  }
  /* noBodyPadding prop — zeroes horizontal padding on the modal body */
  .ant-modal-wrap.app-modal-shell--no-body-padding .ant-modal-body {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
  .ant-modal-wrap.app-modal-shell .ant-modal-body::-webkit-scrollbar {
    width: 6px;
  }
  .ant-modal-wrap.app-modal-shell .ant-modal-body::-webkit-scrollbar-track {
    background: transparent;
  }
  .ant-modal-wrap.app-modal-shell .ant-modal-body::-webkit-scrollbar-thumb {
    border-radius: 3px;
    background: rgba(0, 0, 0, 0.18);
  }
  .ant-modal-wrap.app-modal-shell .ant-modal-body:not(:hover)::-webkit-scrollbar-thumb {
    background: transparent;
  }
  .ant-modal-wrap.app-modal-shell .ant-modal-body:hover::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.22);
  }
  .ant-modal-wrap.app-modal-shell .ant-modal-body .ant-form-item {
    margin-bottom: 12px !important;
  }
  .ant-modal-wrap.app-modal-shell .ant-modal-body .ant-form-item:last-child {
    margin-bottom: 0 !important;
  }
  .ant-modal-wrap.app-modal-shell .ant-modal-footer {
    grid-column: 1 / -1 !important;
    grid-row: 3 !important;
    margin: 0 !important;
    padding: 12px 20px 14px 20px !important;
    font-size: clamp(12px, 0.35vw + 11px, 14px) !important;
    border-top: 1px solid rgba(0, 0, 0, 0.06) !important;
    background: transparent !important;
  }
  .ant-modal-wrap.app-modal-shell .ant-modal-close {
    grid-column: 2 !important;
    grid-row: 1 !important;
    position: static !important;
    inset: unset !important;
    top: auto !important;
    right: auto !important;
    left: auto !important;
    bottom: auto !important;
    z-index: 1 !important;
    align-self: center !important;
    justify-self: end !important;
    margin: 0 !important;
    margin-inline-end: 12px !important;
    margin-inline-start: 4px !important;
    width: 28px !important;
    height: 28px !important;
    border-radius: 6px !important;
    border: 1px solid rgba(0, 0, 0, 0.12) !important;
    background: transparent !important;
    color: rgba(0, 0, 0, 0.65) !important;
    transition: background 0.2s ease, border-color 0.2s ease !important;
  }
  .ant-modal-wrap.app-modal-shell .ant-modal-close .ant-modal-close-x {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 28px !important;
    height: 28px !important;
    line-height: 1 !important;
  }
  .ant-modal-wrap.app-modal-shell .ant-modal-close .ant-modal-close-icon {
    font-size: 10px !important;
    transition: transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
  }
  .ant-modal-wrap.app-modal-shell .ant-modal-close:hover {
    background: rgba(0, 0, 0, 0.08) !important;
    border-color: rgba(0, 0, 0, 0.18) !important;
    color: rgba(0, 0, 0, 0.88) !important;
  }
  .ant-modal-wrap.app-modal-shell .ant-modal-close:hover .ant-modal-close-icon {
    transform: rotate(90deg) !important;
  }

  /* Submission / project flows: no rule under title row */
  .ant-modal-wrap.app-modal-shell .ant-modal-header.submission-modal__header {
    border-bottom: none !important;
    box-shadow: none !important;
  }

  /* Dark mode — class on .ant-modal-root (see AppModal rootClassName) */
  .ant-modal-root.app-modal-shell--dark .ant-modal-mask {
    background: rgba(0, 0, 0, 0.72) !important;
  }
  .ant-modal-root.app-modal-shell--dark .ant-modal-wrap.app-modal-shell .ant-modal-content {
    background: #1e1e1e !important;
    box-shadow:
      0 24px 56px -16px rgba(0, 0, 0, 0.65),
      0 0 0 1px rgba(255, 255, 255, 0.07) !important;
  }
  .ant-modal-root.app-modal-shell--dark .ant-modal-wrap.app-modal-shell .ant-modal-header {
    background: transparent !important;
    border-bottom-color: rgba(255, 255, 255, 0.08) !important;
  }
  .ant-modal-root.app-modal-shell--dark .ant-modal-wrap.app-modal-shell .ant-modal-header.submission-modal__header {
    border-bottom: none !important;
  }
  .ant-modal-root.app-modal-shell--dark .ant-modal-wrap.app-modal-shell .ant-modal-title {
    color: rgba(255, 255, 255, 0.95) !important;
  }
  .ant-modal-root.app-modal-shell--dark .ant-modal-wrap.app-modal-shell .ant-modal-body {
    background: transparent !important;
    color: rgba(255, 255, 255, 0.88) !important;
    scrollbar-color: rgba(255, 255, 255, 0.22) transparent;
  }
  .ant-modal-root.app-modal-shell--dark .ant-modal-wrap.app-modal-shell .ant-modal-body::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.18);
  }
  .ant-modal-root.app-modal-shell--dark .ant-modal-wrap.app-modal-shell .ant-modal-body:not(:hover)::-webkit-scrollbar-thumb {
    background: transparent;
  }
  .ant-modal-root.app-modal-shell--dark .ant-modal-wrap.app-modal-shell .ant-modal-body:hover::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.22);
  }
  .ant-modal-root.app-modal-shell--dark .ant-modal-wrap.app-modal-shell .ant-modal-footer {
    background: transparent !important;
    border-top-color: rgba(255, 255, 255, 0.08) !important;
    padding: 10px 16px 6px 16px !important;
  }
  .ant-modal-root.app-modal-shell--dark .ant-modal-wrap.app-modal-shell .ant-modal-close {
    border-color: rgba(255, 255, 255, 0.12) !important;
    background: transparent !important;
    color: rgba(255, 255, 255, 0.75) !important;
  }
  .ant-modal-root.app-modal-shell--dark .ant-modal-wrap.app-modal-shell .ant-modal-close:hover {
    background: rgba(255, 255, 255, 0.12) !important;
    border-color: rgba(255, 255, 255, 0.22) !important;
    color: rgba(255, 255, 255, 0.95) !important;
  }
  .ant-modal-root.app-modal-shell--dark .ant-modal-wrap.app-modal-shell .ant-modal-close .ant-modal-close-icon {
    color: rgba(255, 255, 255, 0.75) !important;
  }

  /* Dark: form labels + focus affordance inside app modals */
  .ant-modal-root.app-modal-shell--dark .ant-modal-wrap.app-modal-shell .ant-form-item-label > label {
    color: rgba(255, 255, 255, 0.82) !important;
  }
  .ant-modal-root.app-modal-shell--dark .ant-modal-wrap.app-modal-shell .ant-input-affix-wrapper,
  .ant-modal-root.app-modal-shell--dark .ant-modal-wrap.app-modal-shell .ant-input-outlined,
  .ant-modal-root.app-modal-shell--dark .ant-modal-wrap.app-modal-shell .ant-input,
  .ant-modal-root.app-modal-shell--dark .ant-modal-wrap.app-modal-shell .ant-input-number,
  .ant-modal-root.app-modal-shell--dark .ant-modal-wrap.app-modal-shell .ant-select .ant-select-selector,
  .ant-modal-root.app-modal-shell--dark .ant-modal-wrap.app-modal-shell .ant-picker {
    border-radius: 8px !important;
  }
`;

/** sm + md + lg: centered, full-viewport wrap, predictable scroll */
const ModalSizedGlobalStyle = createGlobalStyle`
  .ant-modal-wrap.modal-size-sm,
  .ant-modal-wrap.modal-size-md,
  .ant-modal-wrap.modal-size-lg {
    z-index: var(--app-modal-z-index, ${MODAL_Z_INDEX}) !important;
    height: 100vh !important;
    max-height: 100vh !important;
    padding: 0 !important;
    margin: 0 !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
  }
  /* sm: 400px — equal top/bottom and left/right gap from viewport (${VIEWPORT_INSET}px each side) */
  .ant-modal-wrap.modal-size-sm .ant-modal {
    position: fixed !important;
    left: 50% !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
    width: ${WIDTH_SM}px !important;
    max-width: ${MODAL_MAX_WIDTH} !important;
    max-height: ${MODAL_MAX_HEIGHT} !important;
    margin: 0 !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .ant-modal-wrap.modal-size-sm .ant-modal-content {
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) auto !important;
    grid-template-rows: auto minmax(0, 1fr) auto !important;
    max-height: ${MODAL_MAX_HEIGHT} !important;
    min-height: 0 !important;
    overflow: hidden !important;
    padding: 10px !important;
  }
  .ant-modal-wrap.modal-size-sm .ant-modal-body {
    min-height: 0 !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
  }
  /* md: 560px */
  .ant-modal-wrap.modal-size-md .ant-modal {
    position: fixed !important;
    left: 50% !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
    width: ${WIDTH_MD}px !important;
    max-width: ${MODAL_MAX_WIDTH} !important;
    max-height: ${MODAL_MAX_HEIGHT} !important;
    margin: 0 !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .ant-modal-wrap.modal-size-md .ant-modal-content {
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) auto !important;
    grid-template-rows: auto minmax(0, 1fr) auto !important;
    max-height: ${MODAL_MAX_HEIGHT} !important;
    min-height: 0 !important;
    overflow: hidden !important;
    padding: 10px !important;
  }
  .ant-modal-wrap.modal-size-md .ant-modal-body {
    min-height: 0 !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }
  /* lg: wide modal; height follows content up to max (no fixed 900px empty shell). */
  .ant-modal-wrap.modal-size-lg .ant-modal-render {
    position: fixed !important;
    left: 50% !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
    width: var(--app-modal-lg-width, ${WIDTH_LG}) !important;
    height: auto !important;
    max-width: ${MODAL_MAX_WIDTH} !important;
    max-height: min(${MODAL_MAX_HEIGHT}, 900px) !important;
    margin: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
  }
  [data-modal-lg-root] {
    height: auto !important;
    max-height: inherit !important;
    min-height: 0 !important;
    width: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
  }
  [data-modal-lg-root] .ant-modal {
    top: 0 !important;
    height: auto !important;
    max-height: 100% !important;
    margin: 0 !important;
    padding-bottom: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
    width: 100% !important;
    max-width: 100% !important;
  }
  [data-modal-lg-root] .ant-modal > div {
    flex: 0 1 auto !important;
    min-height: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
  }
  [data-modal-lg-root] .ant-modal-content {
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) auto !important;
    grid-template-rows: auto minmax(0, 1fr) auto !important;
    flex: 0 1 auto !important;
    min-height: 0 !important;
    max-height: inherit !important;
    overflow: hidden !important;
    padding: 10px !important;
  }
  [data-modal-lg-root] .ant-modal-header {
    flex-shrink: 0 !important;
  }
  [data-modal-lg-root] .ant-modal-body {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
  }
  [data-modal-lg-root] .ant-modal-footer {
    flex-shrink: 0 !important;
  }

  /*
   * Narrow or short viewports: avoid vertical centering (top: 50% + translateY(-50%)),
   * which clips the header and makes the inner body feel like it “moves” instead of scrolling.
   * Pin to the top with safe-area padding; body scroll stays inside .ant-modal-body.
   */
  @media (max-width: 576px), (max-height: 560px) {
    .ant-modal-wrap.modal-size-sm .ant-modal {
      top: 50% !important;
      transform: translate(-50%, -50%) !important;
      max-height: calc(
        100vh - ${VIEWPORT_INSET * 2}px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)
      ) !important;
    }
    .ant-modal-wrap.modal-size-sm .ant-modal-content {
      max-height: calc(
        100vh - ${VIEWPORT_INSET * 2}px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)
      ) !important;
    }
    .ant-modal-wrap.modal-size-md .ant-modal {
      top: 50% !important;
      transform: translate(-50%, -50%) !important;
      max-height: calc(
        100vh - ${VIEWPORT_INSET * 2}px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)
      ) !important;
    }
    .ant-modal-wrap.modal-size-md .ant-modal-content {
      max-height: calc(
        100vh - ${VIEWPORT_INSET * 2}px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)
      ) !important;
    }
    .ant-modal-wrap.modal-size-lg .ant-modal-render {
      top: 50% !important;
      transform: translate(-50%, -50%) !important;
      height: auto !important;
      max-height: min(
        calc(
          100vh - ${VIEWPORT_INSET * 2}px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)
        ),
        900px
      ) !important;
    }
  }
`;

const ModalLgRoot = styled.div.attrs({ 'data-modal-lg-root': true })`
    height: auto;
    max-height: 100%;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    & > * {
        flex: 0 1 auto;
        min-height: 0;
        width: 100%;
        max-width: 100%;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }
`;

const wrapClassForSize = (size) => {
  const base = `modal-size-${size} app-modal-shell`;
  return base;
};

const SM_MODAL_PROPS = {
  width: WIDTH_SM,
  centered: true,
  destroyOnHidden: true,
};

const MD_MODAL_PROPS = {
  width: WIDTH_MD,
  centered: true,
  destroyOnHidden: true,
};

const LG_MODAL_MAX_INNER = `min(${MODAL_MAX_HEIGHT}, 900px)`;

const LG_MODAL_PROPS = {
  width: WIDTH_LG,
  centered: true,
  /** Height follows content; cap at viewport / 900px so body scrolls instead of a tall empty shell. */
  style: {
    height: 'auto',
    maxHeight: LG_MODAL_MAX_INNER,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    paddingBottom: 0,
    margin: 0,
  },
  styles: {
    wrapper: {
      height: 'auto',
      maxHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    header: { flexShrink: 0 },
    body: {
      flex: '1 1 auto',
      minHeight: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    footer: { flexShrink: 0 },
  },
};

/**
 * App-wide modal: three sizes (override `size` when needed).
 *
 * - **lg** (default): detail / long content — wide, tall, scrollable body.
 * - **md**: short forms — e.g. create sprint, create task.
 * - **sm**: alerts, confirm, add link — narrow.
 *
 * @param {'sm'|'md'|'lg'} [size='lg']
 * @param {string} [transitionName=''] — empty disables dialog zoom (instant appearance).
 * @param {string} [maskTransitionName=''] — empty disables mask fade.
 */
const AppModal = ({
  size = 'lg',
  /** Only applies when size='lg' */
  lgWidth,
  zIndex,
  open,
  onCancel,
  title,
  footer,
  children,
  styles: stylesProp,
  classNames: classNamesProp,
  rootClassName: rootClassNameProp,
  mousePosition: mousePositionProp,
  /** Default false: mask click does not close (app-wide). Pass true to allow closing on outside click. */
  maskClosable = false,
  transitionName = '',
  maskTransitionName = '',
  /** When true, removes horizontal padding from the modal body. */
  noBodyPadding = false,
  ...rest
}) => {
  const isLg = size === 'lg';
  const themeMode = useThemeStore((s) => s.mode);

  const sizeClass = wrapClassForSize(size);
  const rootClass = [
    sizeClass,
    rootClassNameProp,
    themeMode === 'dark' ? 'app-modal-shell--dark' : '',
  ]
    .filter(Boolean)
    .join(' ');

  /** Omit `mousePosition` unless explicitly passed — antd’s zoom-from-pointer origin caused off-center pop-in. */
  const mousePosition =
    mousePositionProp !== undefined ? mousePositionProp : undefined;
  const mergedClassNames = {
    ...classNamesProp,
    wrapper: [sizeClass, noBodyPadding ? 'app-modal-shell--no-body-padding' : '', classNamesProp?.wrapper].filter(Boolean).join(' '),
  };

  const sizeProps = isLg
    ? {
      ...LG_MODAL_PROPS,
      destroyOnHidden: true,
      modalRender: (node) => <ModalLgRoot>{node}</ModalLgRoot>,
    }
    : size === 'md'
      ? MD_MODAL_PROPS
      : SM_MODAL_PROPS;

  const mergedStyles =
    isLg && sizeProps.styles
      ? {
        ...sizeProps.styles,
        ...stylesProp,
        body: { ...sizeProps.styles?.body, ...stylesProp?.body },
        header: { ...sizeProps.styles?.header, ...stylesProp?.header },
        wrapper: {
          ...sizeProps.styles?.wrapper,
          ...stylesProp?.wrapper,
          ...(lgWidth ? { '--app-modal-lg-width': lgWidth } : {}),
        },
      }
      : stylesProp;

  const effectiveZIndex = typeof zIndex === 'number' ? zIndex : MODAL_Z_INDEX;
  const mergedStylesWithZ = mergedStyles
    ? {
      ...mergedStyles,
      wrapper: {
        ...(mergedStyles?.wrapper ?? {}),
        '--app-modal-z-index': effectiveZIndex,
      },
    }
    : { wrapper: { '--app-modal-z-index': effectiveZIndex } };

  return (
    <>
      <AppModalShellGlobalStyle />
      <ModalSizedGlobalStyle />
      <AntModal
        {...rest}
        {...sizeProps}
        maskClosable={maskClosable}
        open={open}
        onCancel={onCancel}
        title={title}
        footer={footer}
        mousePosition={mousePosition}
        transitionName={transitionName}
        maskTransitionName={maskTransitionName}
        rootClassName={rootClass}
        classNames={mergedClassNames}
        zIndex={effectiveZIndex}
        styles={mergedStylesWithZ}
      >
        {children}
      </AntModal>
    </>
  );
};

export default AppModal;
